/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';

import { SidebarBoundary } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useHoverEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-hover-entry-point-trigger/index.tsx';
import type { Nav4RoadmapsProps } from './ui/roadmaps/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './common/constants.tsx';
import { RoadmapsStatic } from './ui/roadmaps/static/index.tsx';
import { useIsCollectionSelected } from './common/utils/router.tsx';
import { useLocalIsExpanded } from './common/utils/use-local-is-expanded.tsx';
import { asyncActionButtons } from './common/ui/action-buttons/asyncActionButtons.entrypoint.tsx';

/**
 *
 * The continued usage of RLL goes against the guidance given to teams that implemented Nav4:
 * "Do not use RLL. Use entrypoints instead."
 *
 * See https://go/entrypoints-migration-runbook
 *
 * Due to the recent SSR & performance changes made to this module, more consulting is needed with
 * the Magma before it can be fully removed.
 */
const LazySidebarContent = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-roadmaps" */ './ui/roadmaps/index.tsx'
		).then(({ Nav4RoadmapsContainer }) => Nav4RoadmapsContainer),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: true,
	},
);

const EMPTY_EP_PARAMS = {} as const;
// TODO: To be removed with jpd-enable-roadmaps-recents-fe
export const Nav4Roadmaps: React.FC<Nav4RoadmapsProps> = memo((props: Nav4RoadmapsProps) => {
	const { isExpanded, ...dndProps } = props;
	const isCollectionSelected = useIsCollectionSelected();

	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;
	const [localIsExpanded, setLocalIsExpanded] = useLocalIsExpanded(isExpandedValue);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncActionButtons,
		EMPTY_EP_PARAMS,
	);

	const triggerRef = useHoverEntryPointTrigger(entryPointActions);

	return (
		<SidebarBoundary
			id="nav4.sidebar.roadmaps"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			ufoName="nav4-sidebar-roadmaps"
		>
			<RoadmapsStatic
				actionsOnHover={
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="nav4-sidebar-roadmaps-action-menu"
						packageName={PACKAGE_NAME}
						errorFallback="flag"
						teamName={TEAM_NAME}
						runtimeProps={EMPTY_EP_PARAMS}
					/>
				}
				isExpanded={localIsExpanded}
				onExpansionToggle={setLocalIsExpanded}
				triggerRef={triggerRef}
				{...dndProps}
			>
				<Placeholder
					name="nav4-sidebar-roadmaps"
					fallback={isCollectionSelected && <SkeletonNavMenuItems />}
				>
					<LazySidebarContent isExpanded={localIsExpanded} {...dndProps} />
				</Placeholder>
			</RoadmapsStatic>
		</SidebarBoundary>
	);
});
